import { makeAutoObservable } from 'mobx'
import { essaySummaryStore } from './essaySummaryStore'
import {
	rowMatchesEssayStatus,
	rowMatchesNeedsAttention,
} from '../utils/applicationsPageFilters'

const normalizeId = id => String(id || '').trim()

const DEFAULT_PANEL_FILTERS = {
	country: [],
	needsAttention: [],
	essayStatus: [],
}

class ApplicationsStore {
	applications = []
	activeTab = 'all'

	selectedIds = []
	sortKey = null
	sortDir = 'asc'

	isMergeByStudent = false

	activeFilterIds = ['all']

	// Panel filters state
	panelFilters = { ...DEFAULT_PANEL_FILTERS }
	hasPanelFilters = false

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	setApplications(list) {
		this.applications = (list || []).map(x => ({
			...x,
			id: normalizeId(x?.id),
		}))
	}

	patchApplication(id, partial) {
		const key = normalizeId(id)
		if (!key) return
		this.applications = this.applications.map(x =>
			normalizeId(x?.id) === key ? { ...x, ...partial } : x,
		)
	}

	patchByStudentId(studentId, partial) {
		const sid = normalizeId(studentId)
		if (!sid) return
		this.applications = this.applications.map(x =>
			normalizeId(x?.studentId) === sid ? { ...x, ...partial } : x,
		)
	}

	setTab(tabId) {
		this.activeTab = tabId || 'all'
	}

	setActiveFilterIds(ids) {
		const next = (ids || []).map(normalizeId).filter(Boolean)
		this.activeFilterIds = next.length ? next : ['all']
	}

	setSort(key, dir) {
		if (!key) return
		if (dir === 'asc' || dir === 'desc') {
			this.sortKey = key
			this.sortDir = dir
			return
		}
		if (this.sortKey === key) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
		} else {
			this.sortKey = key
			this.sortDir = 'asc'
		}
	}

	clearSort() {
		this.sortKey = null
		this.sortDir = 'asc'
	}

	applyPanelFilters(filters) {
		this.panelFilters = {
			...DEFAULT_PANEL_FILTERS,
			...(filters || {}),
		}

		const hasAny =
			(this.panelFilters.country?.length || 0) > 0 ||
			(this.panelFilters.needsAttention?.length || 0) > 0 ||
			(this.panelFilters.essayStatus?.length || 0) > 0

		this.hasPanelFilters = hasAny
	}

	clearPanelFilters() {
		this.panelFilters = { ...DEFAULT_PANEL_FILTERS }
		this.hasPanelFilters = false
	}

	toggleMergeByStudent() {
		this.isMergeByStudent = !this.isMergeByStudent
	}

	toggleOne(id) {
		const key = normalizeId(id)
		if (!key) return
		if (this.selectedIds.includes(key)) {
			this.selectedIds = this.selectedIds.filter(x => x !== key)
		} else {
			this.selectedIds = [...this.selectedIds, key]
		}
	}

	toggleAll(ids) {
		const list = (ids || []).map(normalizeId).filter(Boolean)
		if (!list.length) return
		if (this.selectedIds.length === list.length) {
			this.selectedIds = []
		} else {
			this.selectedIds = list
		}
	}

	clearSelection() {
		this.selectedIds = []
	}

	get selectedCount() {
		return this.selectedIds.length
	}

	get hasSelection() {
		return this.selectedIds.length > 0
	}

	/**
	 * Get applications for a specific student by student ID
	 * @param {string|number} studentId - Student ID
	 * @returns {Array} Array of application objects for the student
	 */
	getApplicationsByStudentId(studentId) {
		if (!studentId) return []
		const normalizedId = normalizeId(studentId)
		return this.applications.filter(
			app => normalizeId(app?.studentId) === normalizedId
		)
	}

	get filteredApplications() {
		let res = [...this.applications]

		if (this.activeTab === 'inReview') {
			res = res.filter(x => x.result === 'inReview')
		} else if (this.activeTab === 'draft') {
			res = res.filter(x => x.result === 'draft')
		}

		const active = (this.activeFilterIds || []).map(normalizeId)
		const hasNonAll = active.some(x => x !== 'all')

		if (hasNonAll) {
			const monthRef = res?.[0]?.deadline || null

			const parseMonthKey = d => {
				const v = String(d || '').trim()
				if (!v) return null
				const dt = new Date(v)
				if (!Number.isNaN(dt.getTime()))
					return `${dt.getFullYear()}-${dt.getMonth()}`
				return v
			}

			const monthKey = parseMonthKey(monthRef)

			const predicates = {
				deadlineThisMonth: x => {
					if (!monthKey) return true
					return parseMonthKey(x?.deadline) === monthKey
				},
				essayNotReady: x => String(x?.essay || '').toLowerCase() !== 'final',
				statusInProgress: x =>
					String(x?.result || '').toLowerCase() !== 'final',
				stageEssays: x => String(x?.stage || '').toLowerCase() === 'essay',
				missingDocuments: x => Number(x?.missingDocuments || 0) > 0,
			}

			Object.keys(predicates).forEach(key => {
				if (active.includes(key)) res = res.filter(predicates[key])
			})
		}

		// Panel filters (applied by Search button in filter panel)
		if (this.hasPanelFilters) {
			const pf = this.panelFilters
			const essayAggregateMap = essaySummaryStore.aggregateByTargetId
			const essaysByTargetId = essaySummaryStore.essaysByTargetId

			if (pf.country?.length) {
				const selected = new Set(
					pf.country.map(code => String(code || '').trim().toUpperCase()),
				)
				res = res.filter(x =>
					selected.has(String(x?.country || '').trim().toUpperCase()),
				)
			}

			if (pf.needsAttention?.length) {
				res = res.filter(x =>
					rowMatchesNeedsAttention(x, pf.needsAttention, essayAggregateMap),
				)
			}

			if (pf.essayStatus?.length) {
				res = res.filter(x =>
					rowMatchesEssayStatus(
						x,
						pf.essayStatus,
						essayAggregateMap,
						essaysByTargetId,
					),
				)
			}
		}

		if (this.sortKey) {
			const dir = this.sortDir === 'asc' ? 1 : -1
			const key = this.sortKey

			const keyToField = {
				student: 'studentName',
				university: 'university',
				deadline: 'deadline',
			}
			const field = keyToField[key] ?? key

			const getValue = row => row?.[field] ?? row?.[key]

			const parseDate = v => {
				if (v == null) return null
				const d = new Date(v)
				return Number.isNaN(d.getTime()) ? null : d.getTime()
			}

			res.sort((a, b) => {
				const av = getValue(a)
				const bv = getValue(b)
				if (av == null && bv == null) return 0
				if (av == null) return 1
				if (bv == null) return -1

				if (key === 'deadline') {
					const aTime = parseDate(av)
					const bTime = parseDate(bv)
					if (aTime != null && bTime != null) return (aTime - bTime) * dir
				}

				if (key === 'documentsCount' || key === 'missingDocuments') {
					const an = Number(av)
					const bn = Number(bv)
					if (Number.isNaN(an) && Number.isNaN(bn)) return 0
					if (Number.isNaN(an)) return 1
					if (Number.isNaN(bn)) return -1
					return (an - bn) * dir
				}

				return (
					String(av).localeCompare(String(bv), undefined, {
						sensitivity: 'base',
					}) * dir
				)
			})
		}

		return res
	}
}

export const applicationsStore = new ApplicationsStore()
